import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { 
  Phone, Mail, MapPin, Upload, X, CheckCircle2, 
  Home, Building2, Hammer, Wrench, Loader2 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Navigation, Footer } from "@/pages/LandingPage";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  { id: "bathroom", label: { en: "Bathroom Remodeling", es: "Remodelación de Baños" }, icon: Home },
  { id: "kitchen", label: { en: "Kitchen Remodeling", es: "Remodelación de Cocinas" }, icon: Building2 },
  { id: "flooring", label: { en: "Flooring & Tile", es: "Pisos y Azulejo" }, icon: Hammer },
  { id: "painting", label: { en: "Painting", es: "Pintura" }, icon: Wrench },
  { id: "handyman", label: { en: "Handyman Services", es: "Reparaciones Generales" }, icon: Wrench }
];

const budgetRanges = [
  { value: "under_50k", label: { en: "Under $50,000", es: "Menos de $50,000" } },
  { value: "50k_100k", label: "$50,000 - $100,000" },
  { value: "100k_250k", label: "$100,000 - $250,000" },
  { value: "250k_500k", label: "$250,000 - $500,000" },
  { value: "500k_1m", label: "$500,000 - $1,000,000" },
  { value: "over_1m", label: { en: "Over $1,000,000", es: "Más de $1,000,000" } }
];

const timelineOptions = [
  { value: "immediate", label: { en: "Immediate (1-3 months)", es: "Inmediato (1-3 meses)" } },
  { value: "soon", label: { en: "Soon (3-6 months)", es: "Pronto (3-6 meses)" } },
  { value: "planning", label: { en: "Planning (6-12 months)", es: "Planeando (6-12 meses)" } },
  { value: "future", label: { en: "Future (12+ months)", es: "A futuro (12+ meses)" } }
];

export default function ContactPage() {
  const { t, lang } = useLang();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: {
      bathroom: false,
      kitchen: false,
      flooring: false,
      painting: false,
      handyman: false
    },
    description: "",
    budget_range: "",
    timeline: ""
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    }));
    setFiles(prev => [...prev, ...newFiles].slice(0, 5)); // Max 5 files
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    maxFiles: 5
  });

  const removeFile = (index) => {
    setFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleServiceChange = (serviceId, checked) => {
    setFormData(prev => ({
      ...prev,
      service_type: {
        ...prev.service_type,
        [serviceId]: checked
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const selectedServices = Object.values(formData.service_type).some(v => v);
    if (!selectedServices) {
      toast.error(t("Please select at least one service type", "Por favor seleccione al menos un servicio"));
      return;
    }
    
    if (!formData.name || !formData.email || !formData.phone || !formData.description) {
      toast.error(t("Please fill in all required fields", "Por favor llene todos los campos obligatorios"));
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the lead
      const leadResponse = await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service_type: formData.service_type,
        description: formData.description,
        budget_range: formData.budget_range || null,
        timeline: formData.timeline || null,
        language: lang
      });

      const leadId = leadResponse.data.id;

      // Upload images if any
      if (files.length > 0) {
        const formDataFiles = new FormData();
        files.forEach(file => {
          formDataFiles.append('files', file);
        });

        await axios.post(`${API}/leads/${leadId}/images`, formDataFiles, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      setIsSuccess(true);
      toast.success(t("Your inquiry has been submitted successfully!", "¡Su solicitud fue enviada con éxito!"));
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_type: {
          bathroom: false,
          kitchen: false,
          flooring: false,
          painting: false,
          handyman: false
        },
        description: "",
        budget_range: "",
        timeline: ""
      });
      setFiles([]);

    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t("Failed to submit inquiry. Please try again.", "No se pudo enviar la solicitud. Intente de nuevo."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="pt-32 pb-20 min-h-screen flex items-center justify-center">
          <motion.div 
            className="max-w-md mx-auto px-6 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-4 font-['Archivo'] uppercase">
              {t("Thank You!", "¡Gracias!")}
            </h1>
            <p className="text-slate-600 mb-8">
              {t(
                "Your inquiry has been received. Our team will review your project details and get back to you within 24-48 hours.",
                "Recibimos su solicitud. Nuestro equipo revisará los detalles de su proyecto y le responderá en 24-48 horas."
              )}
            </p>
            <Button 
              onClick={() => setIsSuccess(false)}
              className="bg-blue-900 text-white hover:bg-emerald-600 rounded-sm px-8 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm"
              data-testid="submit-another-btn"
            >
              {t("Submit Another Inquiry", "Enviar Otra Solicitud")}
            </Button>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-blue-900 relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 grid-bg opacity-10"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.p 
            className="font-mono text-xs tracking-[0.2em] uppercase text-emerald-400 mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {t("Get Started", "Comience Aquí")}
          </motion.p>
          <motion.h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight uppercase leading-none mb-6 font-['Archivo']"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t("Request a", "Pida Su")}<br />{t("Quote", "Cotización")}
          </motion.h1>
          <motion.p 
            className="text-blue-200 text-base md:text-lg leading-relaxed max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t(
              "Fill out the form below to tell us about your project. Upload photos to help us better understand your needs.",
              "Llene el formulario para contarnos sobre su proyecto. Suba fotos para ayudarnos a entender mejor lo que necesita."
            )}
          </motion.p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-slate-200 p-8 sticky top-32">
                <h2 className="font-bold text-xl text-slate-900 mb-6 font-['Archivo'] uppercase">
                  {t("Contact Info", "Información de Contacto")}
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-900 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-1">{t("Phone", "Teléfono")}</p>
                      <p className="text-slate-900">(503) 991-7412</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-900 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-1">{t("Email", "Correo")}</p>
                      <p className="text-slate-900">finixsterra@gmail.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-900 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-1">{t("Address", "Dirección")}</p>
                      <p className="text-slate-900">Salem, Oregon</p>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-slate-200 mt-8 pt-8">
                  <p className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">{t("Business Hours", "Horario")}</p>
                  <p className="text-slate-700 text-sm">{t("Monday - Friday: 8am - 6pm", "Lunes a Viernes: 8am - 6pm")}</p>
                  <p className="text-slate-700 text-sm">{t("Saturday: 9am - 2pm", "Sábado: 9am - 2pm")}</p>
                  <p className="text-slate-700 text-sm">{t("Sunday: Closed", "Domingo: Cerrado")}</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white border border-slate-200 p-8" data-testid="contact-form">
                <h2 className="font-bold text-xl text-slate-900 mb-6 font-['Archivo'] uppercase">
                  {t("Project Details", "Detalles del Proyecto")}
                </h2>

                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <Label className="form-label mb-2 block">{t("Full Name *", "Nombre Completo *")}</Label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-slate-50 border-slate-200 focus:border-slate-900 rounded-sm"
                      placeholder={t("John Smith", "Juan Pérez")}
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <Label className="form-label mb-2 block">{t("Email Address *", "Correo Electrónico *")}</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-slate-50 border-slate-200 focus:border-slate-900 rounded-sm"
                      placeholder={t("john@example.com", "juan@ejemplo.com")}
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="form-label mb-2 block">{t("Phone Number *", "Número de Teléfono *")}</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="bg-slate-50 border-slate-200 focus:border-slate-900 rounded-sm"
                      placeholder="(503) 991-7412"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                {/* Service Selection */}
                <div className="mb-8">
                  <Label className="form-label mb-4 block">{t("Select Services Needed *", "Seleccione los Servicios que Necesita *")}</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {serviceOptions.map((service) => {
                      const ServiceIcon = service.icon;
                      return (
                        <label 
                          key={service.id}
                          className={`flex items-center gap-3 p-4 border cursor-pointer transition-colors ${
                            formData.service_type[service.id] 
                              ? 'border-emerald-600 bg-emerald-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`service-checkbox-${service.id}`}
                        >
                          <Checkbox
                            checked={formData.service_type[service.id]}
                            onCheckedChange={(checked) => handleServiceChange(service.id, checked)}
                            className="border-slate-300"
                          />
                          <ServiceIcon className={`w-5 h-5 ${formData.service_type[service.id] ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <span className={`text-sm font-medium ${formData.service_type[service.id] ? 'text-slate-900' : 'text-slate-600'}`}>
                            {t(service.label)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Budget & Timeline */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <Label className="form-label mb-2 block">{t("Budget Range", "Presupuesto")}</Label>
                    <Select 
                      value={formData.budget_range || undefined} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, budget_range: value }))}
                    >
                      <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm" data-testid="select-budget">
                        <SelectValue placeholder={t("Select budget range", "Seleccione su presupuesto")} />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetRanges.map((budget) => (
                          <SelectItem key={budget.value} value={budget.value}>{t(budget.label)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label mb-2 block">{t("Project Timeline", "Tiempo del Proyecto")}</Label>
                    <Select 
                      value={formData.timeline || undefined} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, timeline: value }))}
                    >
                      <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm" data-testid="select-timeline">
                        <SelectValue placeholder={t("Select timeline", "Seleccione cuándo")} />
                      </SelectTrigger>
                      <SelectContent>
                        {timelineOptions.map((timeline) => (
                          <SelectItem key={timeline.value} value={timeline.value}>{t(timeline.label)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Project Description */}
                <div className="mb-8">
                  <Label className="form-label mb-2 block">{t("Project Description *", "Descripción del Proyecto *")}</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-slate-50 border-slate-200 focus:border-slate-900 rounded-sm min-h-[150px]"
                    placeholder={t("Please describe your project in detail. Include any specific requirements, materials, or design preferences...", "Describa su proyecto con detalle. Incluya requisitos, materiales o preferencias de diseño...")}
                    required
                    data-testid="input-description"
                  />
                </div>

                {/* File Upload */}
                <div className="mb-8">
                  <Label className="form-label mb-2 block">{t("Upload Photos (Optional)", "Subir Fotos (Opcional)")}</Label>
                  <p className="text-xs text-slate-500 mb-4">{t("Upload up to 5 images (max 10MB each) to help us understand your project", "Suba hasta 5 imágenes (máx. 10MB cada una) para ayudarnos a entender su proyecto")}</p>
                  
                  <div
                    {...getRootProps()}
                    className={`upload-zone p-8 text-center cursor-pointer ${isDragActive ? 'drag-over' : ''}`}
                    data-testid="file-upload-zone"
                  >
                    <input {...getInputProps()} />
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <p className="text-sm text-slate-600 mb-2">
                      {isDragActive ? t('Drop files here...', 'Suelte los archivos aquí...') : t('Drag & drop images here, or click to select', 'Arrastre imágenes aquí, o haga clic para seleccionar')}
                    </p>
                    <p className="text-xs text-slate-400">{t("JPEG, PNG, WebP, GIF (max 10MB)", "JPEG, PNG, WebP, GIF (máx. 10MB)")}</p>
                  </div>

                  {/* File Preview */}
                  {files.length > 0 && (
                    <div className="image-preview-grid mt-4" data-testid="file-preview">
                      {files.map((file, index) => (
                        <div key={index} className="relative aspect-square bg-slate-100 overflow-hidden group">
                          <img src={file.preview} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-file-${index}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-900 text-white hover:bg-emerald-600 rounded-sm px-8 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm disabled:opacity-50"
                  data-testid="submit-form-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("Submitting...", "Enviando...")}
                    </>
                  ) : (
                    t("Submit Inquiry", "Enviar Solicitud")
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
